// HospitalDetail.jsx
import React, { useEffect } from "react";
import { useParams } from "react-router-dom";
import { useAllHospitals } from "../hooks/useHospitals";
import { hospitalNameToSlug } from "../utils/hospitalUtils";
import { testAllRecognitionImages } from "../utils/imageUtils";
import Header from "../components/header";
import Footer from '../components/footer';
import FloatingChatButton from '../components/FloatingChatButton';
import ContactForm from "../components/ContactForm";
import HospitalDoctorsCarousel from "../components/HospitalDoctorsCarousel_new";
import HospitalSpecialities from "../components/HospitalSpecialities";
import SimilarHospitalsCarousel from "../components/SimilarHospitalsCarousel";

const HospitalDetail = () => {
  const { id } = useParams(); // This will now be the hospital name slug
  const { hospitals: allHospitals, loading, error } = useAllHospitals();
  
  // Find hospital by comparing slug of hospital name with the URL parameter
  const hospital = allHospitals.find((h) => hospitalNameToSlug(h.name) === id);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Test recognition images when hospital data is available
  useEffect(() => {
    if (hospital && hospital.recognitions && hospital.recognitions.length > 0) {
      console.log(`🏥 Testing recognition images for ${hospital.name}...`);
      testAllRecognitionImages(hospital.recognitions);
    }
  }, [hospital]);

  if (loading) {
    return (
      <div className="min-h-screen flex flex-col">
        <Header />
        <div className="text-center p-6 sm:p-10 flex-grow flex items-center justify-center">
          <h2 className="text-xl sm:text-2xl lg:text-3xl font-bold">Loading hospital details...</h2>
        </div>
        <Footer />
        <FloatingChatButton />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex flex-col">
        <Header />
        <div className="text-center p-6 sm:p-10 flex-grow flex items-center justify-center">
          <div>
            <h2 className="text-xl sm:text-2xl lg:text-3xl font-bold text-red-600">Error loading hospital</h2>
            <p className="text-gray-600 mt-2 text-sm sm:text-base">{error}</p>
          </div>
        </div>
        <Footer />
        <FloatingChatButton />
      </div>
    );
  }

  if (!hospital) {
    return (
      <div className="min-h-screen flex flex-col">
        <Header />
        <div className="text-center p-6 sm:p-10 flex-grow flex items-center justify-center">
          <h2 className="text-xl sm:text-2xl lg:text-3xl font-bold">Hospital not found</h2>
        </div>
        <Footer />
        <FloatingChatButton />
      </div>
    );
  }

  return (
    <div className="bg-white">
      <Header />

      {/* Banner */}
      <section className="w-full py-6 px-4 md:py-8 lg:py-12">
        <div className="container mx-auto flex flex-col lg:flex-row gap-6 md:gap-8 lg:gap-12 items-center">
          {/* Hospital Image */}
          <div className="lg:w-1/2 w-full">
            <div className="rounded-xl lg:rounded-2xl overflow-hidden shadow-lg">
              <img
                src={hospital.mainImage}
                alt={hospital.name}
                className="w-full h-48 sm:h-56 md:h-64 lg:h-80 xl:h-96 object-cover"
              />
            </div>
          </div>
          
          {/* Hospital Information */}
          <div className="lg:w-1/2 w-full lg:pl-8">
            <h1 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-blue-600 mb-4 sm:mb-6 leading-tight">
              {hospital.name}
            </h1>
            
            <p className="text-gray-700 text-base sm:text-lg md:text-xl leading-relaxed mb-6 sm:mb-8 max-w-2xl">
              {hospital.description}
            </p>
            
            {/* Action Buttons */}
            <div className="flex flex-col sm:flex-row gap-3 sm:gap-4">
              <button
                onClick={() =>
                  document
                    .getElementById("contact-form")
                    ?.scrollIntoView({ behavior: "smooth" })
                }
                className="bg-blue-600 hover:bg-blue-700 text-white font-semibold px-6 sm:px-8 py-3 rounded-full transition-colors duration-200 text-center text-sm sm:text-base"
              >
                Get A Call Back
              </button>
              <a
                href="https://wa.me/9599222655"
                target="_blank"
                className="border-2 border-gray-300 hover:border-green-500 text-gray-700 hover:text-green-600 font-semibold px-6 sm:px-8 py-3 rounded-full transition-all duration-200 text-center flex items-center justify-center gap-2 text-sm sm:text-base"
                rel="noopener noreferrer"
              >
                <i className="ri-whatsapp-line"></i>
                Chat Now
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* About Hospital */}
      <section className="py-12 sm:py-16 bg-white">
        <div className="container mx-auto px-4">
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-900 mb-6 sm:mb-8 text-left">
            About the Hospital
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 sm:gap-12 items-start">
            {/* Left Column: About + Recognitions */}
            <div>
              <p className="text-gray-700 text-base sm:text-lg leading-relaxed mb-8 sm:mb-12">
                {hospital.about}
              </p>

              <h3 className="text-xl sm:text-2xl font-bold text-blue-600 mb-4 sm:mb-6">
                Recognitions
              </h3>
              <div className="flex flex-wrap gap-4 sm:gap-6">
                {(hospital.recognitions || []).length > 0 ? (
                  hospital.recognitions.map((recognition, index) => {
                    // Handle both object and string formats
                    const imageUrl = typeof recognition === 'string' ? recognition : recognition.image;
                    const description = typeof recognition === 'object' ? recognition.description : '';
                    
                    return (
                      <div
                        key={index}
                        className="w-12 h-12 sm:w-16 sm:h-16 flex items-center justify-center"
                        title={description || `Recognition ${index + 1}`}
                      >
                        <img
                          src={imageUrl}
                          alt={`recognition-${index}`}
                          className="max-h-full max-w-full object-contain"
                          onError={(e) => {
                            console.error(`Failed to load recognition image: ${imageUrl}`);
                            e.target.style.display = 'none';
                            const errorDiv = document.createElement('div');
                            errorDiv.className = 'text-xs text-gray-400 text-center flex items-center justify-center w-full h-full';
                            errorDiv.innerHTML = '🏆<br/>Award';
                            e.target.parentElement.appendChild(errorDiv);
                          }}
                          onLoad={() => {
                            console.log(`Successfully loaded recognition image: ${imageUrl}`);
                          }}
                        />
                      </div>
                    );
                  })
                ) : (
                  <div className="text-gray-500 italic text-sm sm:text-base">
                    No recognitions available for this hospital.
                  </div>
                )}
              </div>
            </div>

            {/* Right Column: Specialities */}
            <div>
              <h3 className="text-xl sm:text-2xl font-semibold text-blue-700 mb-3 sm:mb-4">
                Specialities
              </h3>
              <HospitalSpecialities specialities={hospital.specialities} />
            </div>
          </div>
        </div>
      </section>

      {/* Gallery */}
      <section className="py-8 sm:py-10 bg-white">
        <div className="container mx-auto px-4">
          <h3 className="text-xl sm:text-2xl font-bold text-center mb-4 sm:mb-6">
            Hospital <span className="text-blue-600">Gallery</span>
          </h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4">
            {(hospital.gallery || []).map((img, index) => (
              <img
                key={index}
                src={img}
                alt={`gallery-${index}`}
                className="w-full h-32 sm:h-40 md:h-48 object-cover rounded"
              />
            ))}
          </div>
        </div>
      </section>

      {/* Hospital Infrastructure */}
      <section className="py-12 sm:py-16 bg-white">
        <div className="container mx-auto px-4">
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-900 mb-8 sm:mb-16 text-center">
            Hospital <span className="text-blue-600">Infrastructure</span>
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 sm:gap-8">
            <div>
              <h3 className="text-xl sm:text-2xl font-bold mb-4 sm:mb-6">Amenities & Facilities</h3>
              <ul className="list-disc pl-4 sm:pl-6 text-gray-700 space-y-1 sm:space-y-2 text-base sm:text-lg leading-relaxed">
                {(hospital.amenities || []).map((a, i) => (
                  <li key={i}>{a}</li>
                ))}
              </ul>
            </div>
            <div id="contact-form">
              <h3 className="text-lg sm:text-xl font-bold mb-3">
                Get Treated at {hospital.name}
              </h3>
              <ContactForm formType="hospital-inquiry" />
            </div>
          </div>
        </div>
      </section>

      {/* Treatments Available */}
      {/* <section className="py-10 bg-white">
        <div className="container mx-auto px-4">
          <h3 className="text-2xl font-bold text-center mb-4">
            Treatments <span className="text-blue-600">Available</span>
          </h3>
          <ul className="flex flex-wrap justify-center gap-4 mt-6">
            {(hospital.treatments || []).map((t, i) => (
              <li
                key={i}
                className="bg-blue-50 px-4 py-2 rounded text-blue-700 shadow"
              >
                {t}
              </li>
            ))}
          </ul>
        </div>
      </section> */}

      {/* Doctors */}
      <HospitalDoctorsCarousel hospitalName={hospital.name} />

      {/* Similar Hospitals */}
      <SimilarHospitalsCarousel currentHospital={hospital} />

      <Footer />
      <FloatingChatButton />
    </div>
  );
};

export default HospitalDetail;
